'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["namespace", "inlineToolbarContainer", "useAnimatedToolbar", "hasFocus", "forwardRef", "showToolbarShepherd"];
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import classnames from 'classnames';
import TinymceToolbar from './TinymceToolbar';
import styled, { keyframes } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
const slideOut = ({
  theme
}) => keyframes(["0%{transform:translate3d(0,0,0);box-shadow:0 1px 5px ", "44;}100%{transform:translate3d(0,-100%,0);box-shadow:0 1px 5px ", "00;}"], theme.color['text-core-default'], theme.color['text-core-default']);
const slideIn = ({
  theme
}) => keyframes(["0%{transform:translate3d(0,-100%,0);box-shadow:0 1px 5px ", "00;}100%{transform:translate3d(0,0,0);box-shadow:0 1px 5px ", "44;}"], theme.color['text-core-default'], theme.color['text-core-default']);
const StaticStyledToolbar = styled(TinymceToolbar).withConfig({
  displayName: "InlineToolbar__StaticStyledToolbar",
  componentId: "sc-19u0wsw-0"
})(["box-shadow:0 1px 5px ", ";"], ({
  theme
}) => `${theme.color['text-core-default']}33`);
const AnimatedStyledToolbar = styled(TinymceToolbar).withConfig({
  displayName: "InlineToolbar__AnimatedStyledToolbar",
  componentId: "sc-19u0wsw-1"
})(["animation-name:", ";animation-duration:", ";animation-timing-function:", ";animation-fill-mode:forwards;"], props => props.hasFocus ? slideIn(props) : slideOut(props), props => props.hasFocus ? '.375s' : '.25s', props => props.hasFocus ? 'ease-out' : 'ease-in');
export default function InlineToolbar(props) {
  const {
      namespace,
      inlineToolbarContainer,
      useAnimatedToolbar,
      hasFocus,
      forwardRef,
      showToolbarShepherd
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const StyledToolbar = useAnimatedToolbar ? AnimatedStyledToolbar : StaticStyledToolbar;
  const editor = props.editor;
  const className = classnames('mce-toolbar', namespace);
  const styledToolbar = useRef();
  useEffect(() => {
    const toolbar = styledToolbar.current;
    if (hasFocus) {
      toolbar.style.display = 'flex';
    }
    const handleAnimationEnd = () => {
      if (!hasFocus) {
        toolbar.style.display = 'none';
      }
      editor.fire('ToolbarAnimationEnd', {
        hasFocus
      });
    };
    toolbar.addEventListener('animationend', handleAnimationEnd);
    return () => {
      toolbar.removeEventListener('animationend', handleAnimationEnd);
    };
  }, [editor, inlineToolbarContainer, hasFocus]);
  useEffect(() => {
    editor.inlineToolbarContainer = inlineToolbarContainer;
    return () => {
      editor.inlineToolbarContainer = null;
    };
  }, [editor, inlineToolbarContainer]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx("div", {
      tabIndex: "-1",
      role: "application",
      className: className,
      children: /*#__PURE__*/_jsx(StyledToolbar, Object.assign({}, rest, {
        forwardRef: el => {
          if (forwardRef) {
            forwardRef(el);
          }
          styledToolbar.current = el;
        },
        hasFocus: hasFocus,
        centerControls: true,
        showToolbarShepherd: showToolbarShepherd && hasFocus
      }))
    })
  );
}
InlineToolbar.propTypes = {
  inlineToolbarContainer: PropTypes.instanceOf(Element).isRequired,
  useAnimatedToolbar: PropTypes.bool,
  editor: PropTypes.object.isRequired,
  hasFocus: PropTypes.bool.isRequired,
  namespace: PropTypes.string,
  forwardRef: PropTypes.func,
  showToolbarShepherd: PropTypes.bool
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
InlineToolbar.defaultProps = {
  useAnimatedToolbar: false
};